<script setup lang="ts">
import { ElCollapse } from 'element-plus'

/**
 * Sections that fold, holding `CollapseItem`s. `v-model` is the name (or names) of what is
 * open — an array normally, one name under `accordion`.
 *
 * A form split across sections drives that model with `useErrorSections`, so a folded
 * section holding a rejected field opens itself; a form whose sections are tabs wants
 * `Tabs` and `useErrorSection` instead. `flush` drops the outer rules, for a collapse that
 * fills a card.
 */
withDefaults(
  defineProps<{
    modelValue?: string | number | Array<string | number>
    accordion?: boolean
    flush?: boolean
  }>(),
  {
    accordion: false,
    flush: false
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', v: string | number | Array<string | number>): void
}>()
</script>

<template>
  <ElCollapse
    :model-value="modelValue"
    :accordion="accordion"
    :class="{ 'collapse--flush': flush }"
    v-bind="$attrs"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <slot />
  </ElCollapse>
</template>

<style lang="scss" scoped>
// Element's header is a fixed 48px row with a matching 48px line-height, so a title long
// enough to wrap spills out of its own row and the second line lands on the item above —
// which is what every FAQ question longer than one line did. The row grows with its title
// instead: 48px is a floor, and the lines sit at a readable distance from each other.
:deep(.el-collapse-item__header) {
  height: auto;
  min-height: 48px;
  padding: 12px 0;
  line-height: 1.5;
}

.collapse--flush {
  border-top: 0;
  border-bottom: 0;

  :deep(.el-collapse-item:last-child .el-collapse-item__header),
  :deep(.el-collapse-item:last-child .el-collapse-item__wrap) {
    border-bottom: 0;
  }
}
</style>
